import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { fadeInUp, staggerContainer } from "@/lib/constants";

const residentialServices = [
  {
    title: "Custom Shower Glass",
    desc: "Frameless, Semi-Frameless, Sliding & Hinged Enclosures",
    image: "https://images.unsplash.com/photo-1630699144565-a42d8db6da02?w=800&q=80",
    href: "/services/custom-shower-glass",
    large: true,
  },
  {
    title: "Window Glass Replacement",
    desc: "Insulated & single-pane glass repair",
    image: "https://images.unsplash.com/photo-1600585154340-be6161a56a0c?w=800&q=80",
    href: "/services/window-glass-replacement",
  },
  {
    title: "Custom Mirrors",
    desc: "Bathroom, vanity, gym & closet mirrors",
    image: "https://images.unsplash.com/photo-1763485957127-5645e9348426?w=800&q=80",
    href: "/services/custom-mirrors",
  },
];

const commercialServices = [
  {
    title: "Storefront & Commercial Glass",
    desc: "Storefronts, curtain walls & aluminum doors",
    image: "https://images.unsplash.com/photo-1758913864864-7cc0d494a944?w=800&q=80",
    href: "/services/commercial-glass",
    wide: true,
  },
];

const ServiceCard = ({ service, index, isLarge, isWide }) => {
  const className = `bento-item relative group cursor-pointer ${
    isLarge ? "bento-item-large" : ""
  } ${isWide ? "bento-item-wide" : ""} min-h-[280px]`;

  return (
    <motion.div variants={fadeInUp}>
      <Link to={service.href} data-testid={`service-card-${index}`} className={className + " block"}>
        <img
          src={service.image}
          alt={service.title}
          className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent" />
        <div className="absolute bottom-0 left-0 right-0 p-6 md:p-8">
          <h3 className="font-heading text-2xl md:text-3xl font-medium text-white mb-2">
            {service.title}
          </h3>
          <p className="font-body text-white/80 text-sm md:text-base mb-3">
            {service.desc}
          </p>
          <span className="inline-flex items-center gap-2 text-white font-body text-sm font-medium group-hover:gap-3 transition-all">
            Learn More <ArrowRight className="w-4 h-4" />
          </span>
        </div>
      </Link>
    </motion.div>
  );
};

const ServicesSection = () => {
  return (
    <section id="services" data-testid="services-section" className="section-spacing bg-white">
      <div className="section-container">
        <motion.div
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: "-100px" }}
          variants={staggerContainer}
          className="mb-10"
        >
          <motion.span
            variants={fadeInUp}
            className="inline-block font-accent text-sm font-semibold tracking-widest uppercase text-[#0F4C81] mb-4"
          >
            Our Services
          </motion.span>
          <motion.h2
            variants={fadeInUp}
            className="font-heading text-4xl md:text-5xl font-medium tracking-tight text-[#1A202C] mb-4"
          >
            Comprehensive Glass Solutions
          </motion.h2>
          <motion.p
            variants={fadeInUp}
            className="font-body text-lg text-[#4B5563] max-w-3xl"
          >
            Brunswick Glass offers comprehensive glass solutions for both residential and commercial clients along the coast. We specialize in delivering high-quality craftsmanship, precise installations, and dependable service.
          </motion.p>
        </motion.div>

        {/* Residential Services */}
        <motion.div
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: "-100px" }}
          variants={staggerContainer}
          className="mb-4"
        >
          <motion.h3 variants={fadeInUp} className="font-heading text-2xl font-medium text-[#1A202C] mb-4">
            Residential Services
          </motion.h3>
        </motion.div>

        <motion.div
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: "-50px" }}
          variants={staggerContainer}
          className="bento-grid mb-10"
        >
          <ServiceCard service={residentialServices[0]} index={0} isLarge={true} />
          <ServiceCard service={residentialServices[1]} index={1} />
          <ServiceCard service={residentialServices[2]} index={2} />
        </motion.div>

        {/* Commercial Services */}
        <motion.div
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: "-100px" }}
          variants={staggerContainer}
          className="mb-4"
        >
          <motion.h3 variants={fadeInUp} className="font-heading text-2xl font-medium text-[#1A202C] mb-4">
            Commercial Services
          </motion.h3>
        </motion.div>

        <motion.div
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: "-50px" }}
          variants={staggerContainer}
          className="grid grid-cols-1 gap-4"
        >
          <ServiceCard service={commercialServices[0]} index={3} isWide={true} />
        </motion.div>

        {/* Additional Services List - Centered */}
        <motion.div
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: "-50px" }}
          variants={staggerContainer}
          className="mt-10 grid md:grid-cols-2 gap-8"
        >
          <motion.div variants={fadeInUp} className="text-center">
            <h4 className="font-heading text-xl font-medium text-[#1A202C] mb-4">More Residential Services</h4>
            <div className="flex flex-wrap justify-center gap-3">
              <span className="px-4 py-2 bg-[#F9F9F7] font-body text-[#4B5563] text-sm">Screen Repair</span>
              <span className="px-4 py-2 bg-[#F9F9F7] font-body text-[#4B5563] text-sm">Table Top Glass</span>
              <span className="px-4 py-2 bg-[#F9F9F7] font-body text-[#4B5563] text-sm">Glass Shelves</span>
              <span className="px-4 py-2 bg-[#F9F9F7] font-body text-[#4B5563] text-sm">Marine & Boat Glass</span>
            </div>
          </motion.div>
          <motion.div variants={fadeInUp} className="text-center">
            <h4 className="font-heading text-xl font-medium text-[#1A202C] mb-4">More Commercial Services</h4>
            <div className="flex flex-wrap justify-center gap-3">
              <span className="px-4 py-2 bg-[#F9F9F7] font-body text-[#4B5563] text-sm">Curtain Walls</span>
              <span className="px-4 py-2 bg-[#F9F9F7] font-body text-[#4B5563] text-sm">Aluminum Doors</span>
              <span className="px-4 py-2 bg-[#F9F9F7] font-body text-[#4B5563] text-sm">Glass Handrails</span>
              <span className="px-4 py-2 bg-[#F9F9F7] font-body text-[#4B5563] text-sm">Gym Mirrors</span>
              <span className="px-4 py-2 bg-[#F9F9F7] font-body text-[#4B5563] text-sm">System Repairs</span>
            </div>
          </motion.div>
        </motion.div>
      </div>
    </section>
  );
};

export default ServicesSection;
